import { useMemo, useState } from 'react';
import { CONCEPTS, codeOf } from './registry';
import { CATEGORY_ORDER, addFromLibrary } from './library';
import type { ConceptKind } from './kinds';
import type { ConceptCategory, ConceptDef } from './types';
import { useIsAdmin } from './useIsAdmin';
import { useRatings } from './useRatings';
import { useLearner } from '../learners/LearnerContext';
import { translate } from '../i18n/i18n';

const categoryRank = (c: ConceptCategory) => CATEGORY_ORDER.indexOf(c);

export function useLibrary() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<ConceptCategory | ''>('');
  const [kind, setKind] = useState<ConceptKind | ''>('');
  const [sort, setSort] = useState<'category' | 'easiest' | 'hardest'>('category');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [added, setAdded] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const isAdmin = useIsAdmin();
  const ratings = useRatings();
  const { learnerId } = useLearner();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CONCEPTS
      .filter((c) =>
        (!category || c.category === category) &&
        (!kind || c.kind === kind) &&
        (!q ||
          c.label.toLowerCase().includes(q) ||
          codeOf(c.id).toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q)),
      )
      .sort((a, b) => {
        const byCategory = categoryRank(a.category) - categoryRank(b.category) || a.label.localeCompare(b.label);
        if (sort === 'category') return byCategory;
        const da = ratings.difficultyOf(a.id), db = ratings.difficultyOf(b.id);
        if (da === null || db === null) return da === db ? byCategory : da === null ? 1 : -1;
        return (sort === 'easiest' ? da - db : db - da) || byCategory;
      });
  }, [query, category, kind, sort, ratings.difficultyOf]);

  const counts = useMemo(() => {
    const m = new Map<ConceptCategory, number>();
    for (const c of CONCEPTS) {
      if (kind && c.kind !== kind) continue;
      m.set(c.category, (m.get(c.category) ?? 0) + 1);
    }
    return m;
  }, [kind]);

  async function add(c: ConceptDef) {
    setBusyId(c.id);
    setActionError(null);
    try {
      const p = await addFromLibrary(c, learnerId);
      setAdded((cur) => ({ ...cur, [c.id]: p.id }));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : translate('Could not add "{program}".', { program: c.label }));
    } finally {
      setBusyId(null);
    }
  }

  return {
    total: CONCEPTS.length,
    rows,
    counts,
    query,
    setQuery,
    category,
    setCategory,
    kind,
    setKind,
    sort,
    setSort,
    busyId,
    added,
    actionError,
    add,
    isAdmin,
    difficultyOf: ratings.difficultyOf,
    rate: ratings.rate,
    ratingsError: ratings.error,
  };
}

export type LibraryModel = ReturnType<typeof useLibrary>;
